import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Divider
} from '@mui/material';
import {
    Close,
    Lock,
    Password,
    Pin
} from '@mui/icons-material';

const SecurityCenterModal = ({ open, onClose }) => {
    const securityOptions = [
        {
            title: 'Change Password',
            description: 'Update your account password for enhanced security',
            icon: Password,
            color: '#22c55e',
            gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            action: () => {
                // Navigate to password change page
                window.location.href = '/change-password';
            }
        },
        {
            title: 'Change Account PIN',
            description: 'Modify your 4-digit security PIN for transactions',
            icon: Pin,
            color: '#3b82f6',
            gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            action: () => {
                // Navigate to PIN change page
                window.location.href = '/change-pin';
            }
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: 'rgba(15, 23, 42, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    borderRadius: 4,
                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                    borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)',
                            }}
                        >
                            <Lock sx={{ color: 'white', fontSize: '1.5rem' }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#f8fafc',
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #a78bfa 0%, #ec4899 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                Security Center
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                                Manage your account security settings
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#94a3b8',
                            '&:hover': {
                                color: '#f8fafc',
                                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            }
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 4 }}>
               

                <Grid style={{marginTop: 30}} container spacing={3} justifyContent="center">
                    {securityOptions.map((option, index) => {
                        const Icon = option.icon;
                        return (
                            <Grid item xs={12} sm={5} key={index}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        background: 'rgba(15, 23, 42, 0.6)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        borderRadius: 3,
                                        cursor: 'pointer',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: `0 15px 35px rgba(${option.color === '#22c55e' ? '34, 197, 94' :
                                                option.color === '#3b82f6' ? '59, 130, 246' :
                                                    option.color === '#f59e0b' ? '245, 158, 11' : '139, 92, 246'}, 0.2)`,
                                            border: `1px solid ${option.color}40`,
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '4px',
                                            background: option.gradient,
                                        }
                                    }}
                                    onClick={option.action}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: 2,
                                                    background: option.gradient,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: `0 8px 24px ${option.color}30`,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Icon sx={{ color: 'white', fontSize: '1.5rem' }} />
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 600, mb: 1 }}>
                                                    {option.title}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.5 }}>
                                                    {option.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

            </DialogContent>

            <DialogActions sx={{ p: 3, background: 'rgba(15, 23, 42, 0.3)' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderColor: 'rgba(139, 92, 246, 0.4)',
                        color: '#a78bfa',
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: '#a78bfa',
                            backgroundColor: 'rgba(139, 92, 246, 0.1)',
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SecurityCenterModal;